import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Images } from "./Images";
import { RemoveAccount_api } from "../Setting/SettingReactAPI";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";

const DeleteAccount = ({ show, onClose, onConfirm }) => {
  const { t } = useTranslation("common");
  // Centralised success/error toaster + orange alert-popup handling.
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  // On a 423 ("user inactive") we hide this modal so only the shared orange
  // popup is shown; its confirm/close then logs the user out to /login.
  const [dismissed, setDismissed] = useState(false);

  // The exact phrase the user must type to enable removal (e.g. "DELETE ACCOUNT").
  const keyword = t("type_here")+ "...";
  // Strict guard for a destructive action: case-sensitive, raw exact match with
  // NO trim and NO surrounding-whitespace tolerance. Any leading/trailing space
  // (e.g. "DELETE ACCOUNT   ") is rejected. Matches EITHER the localized keyword
  // or the exact English literal "DELETE ACCOUNT", regardless of the active locale.
  const isConfirmed =
    confirmText === t("react_delete_account_keyword") ;

  const handleConfirm = async () => {
    if (!isConfirmed || loading) return;

    setLoading(true);

    try {
      const res = await RemoveAccount_api();

      // Success toaster on 200, error toaster / orange alert popup otherwise.
      handleApiUiResponse(res, {
        successToaster: 1,
        errorToaster: 1,
        alertPopup: 1,
      });

      if (Number(res?.Data?.ResponseCode) === 200) {
        onConfirm?.(confirmText);
        // Keep `loading` true so the buttons stay disabled, and delay the
        // hard-logout so the success toaster is visible before we clear the
        // session and navigate back to the login screen.
        setTimeout(() => {
          sessionStorage.clear();
          localStorage.clear();
          window.location.href = "/";
        }, 500);
        return;
      }

      if (Number(res?.Data?.ResponseCode) === 423) {
        // Hide the Remove-account modal so only the orange "user inactive"
        // popup remains; its confirm/close logs out to /login (shared handler).
        // Keep `loading` true — the modal is gone and a redirect follows.
        setDismissed(true);
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error("Remove account failed:", err);
      // No response object on a network/throw error — synthesize one so the
      // shared handler shows the error toaster.
      handleApiUiResponse(
        { Data: { ResponseCode: 0, ErrorMessage: t("something_went_wrong") } },
        { successToaster: 0, errorToaster: 1, alertPopup: 0 }
      );
      setLoading(false);
    }
  };

  // Safe early return AFTER hooks (mirrors NewMeetingModal pattern)
  if (!show) return null;

  return (
    <>
    {!dismissed && (
    <div
      className="modal deleteaccountpopup d-block"
      id="deleteAccountModal"
      style={{ background: "rgba(217, 217, 217, 0.59)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-custom-width-long">
        <div className="modal-content modal-radius">
          <div className="modal-body">

            {/* Close Button (green circle via .close-modal) */}
            <div className="close-modal">
              <img
                src={Images.Close}
                alt="close"
                onClick={onClose}
                style={{ cursor: "pointer" }}
              />
            </div>

            {/* Title */}
            <div className="pt-3">
              <h5 className="popup-title text-red text-center mb-0 pb-2">
                {t("react_delete_account_confirmation")}
              </h5>
            </div>

            <hr />

            {/* Body */}
            <div className="modal-des">
              <p>{t("react_delete_account_description")}</p>
              <ul>
                <li>{t("removal_requestsentbyemail")}</li>
                <li>
                  {t("removal_requestsentbyemail_review")}
                  <ol type="a" className="mt-2">
                    <li>{t("removal_requestsentbyemail_revoked")}</li>
                    <li>{t("removal_requestsentbyemail_booking")}</li>
                    <li>{t("removal_requestsentbyemail_booking_nolonger")}</li>
                    <li>{t("removal_requestsentbyemail_account_removed")}</li>
                  </ol>
                </li>
              </ul>

              {/* Email confirmation */}
              <form className="form-elements">
              <label className="popup-inner-title mt-3">
                {(() => {
                  // "Please type %sDELETE ACCOUNT%s to confirm" -> keyword shown bold.
                  const parts = t("react_s_delete_account_s_confirmation").split("%s");
                  return parts.length === 3 ? (
                    <>
                      {parts[0]}
                      <strong>"{parts[1]}"</strong>
                      {parts[2]}
                    </>
                  ) : (
                    t("react_s_delete_account_s_confirmation").replace(/%s/g, "")
                  );
                })()}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={keyword}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
              </form>

              {/* Footer */}
              <div className="d-flex justify-content-between align-items-center mt-5">
                <button
                  type="button"
                  className="main-btn outline-gray-btn"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  className="main-btn fill-red-btn bg-red"
                  onClick={handleConfirm}
                  disabled={!isConfirmed || loading}
                  style={{
                    opacity: isConfirmed && !loading ? 1 : 0.5,
                    cursor: isConfirmed && !loading ? "pointer" : "not-allowed",
                  }}
                >
                  {t("EmployeeRemoveMyAccountLink")}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    )}
    {renderApiUi()}
    </>
  );
};

export default DeleteAccount;
